import React from 'react';
import { CloseOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { BaseAlertBanner, BaseButton } from '@components';
import { AbsoluteWrapper as Wrapper } from '@components/alert-banner/styled';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks';
import { useTaskCsv } from '@hooks/useTaskCsv';
import { useLingui } from '@lingui/react/macro';
import {
    clearTaskCsvImportState,
    setIsTaskCsvFilePickerRequested,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/model/creatorElements.slice.ts';

import { Actions, Description, DescriptionWrapper } from './styled';

interface Props {
    filename: string;
}

export const TaskCsvImportFailedBanner: React.FC<Props> = ({ filename }) => {
    const { t } = useLingui();
    const dispatch = useAppDispatch();

    const { exportTaskCsvTemplate, downloadTaskCsvIssues } = useTaskCsv();

    const taskCsvIssuesFileContent = useAppSelector(
        (state) => state.adminCenterCreatorElements.taskCsvIssuesFileContent,
    );

    const handleTryDifferentFile = () => {
        void dispatch(clearTaskCsvImportState());
        void dispatch(setIsTaskCsvFilePickerRequested(true));
    };

    const handleClose = () => {
        void dispatch(clearTaskCsvImportState());
    };

    return (
        <Wrapper $width="50%">
            <BaseAlertBanner
                closable={{
                    closeIcon: <CloseOutlined />,
                    onClose: handleClose,
                }}
                type="error"
                title={`${filename} - import failed`}
                description={
                    <DescriptionWrapper>
                        <Description>
                            <div>{t`The CSV file could not be imported. Please check the following:`}</div>

                            <ul>
                                <li>{t`File format is valid CSV`}</li>
                                <li>{t`File is not corrupted or empty`}</li>
                            </ul>
                        </Description>

                        <Actions>
                            <BaseButton onClick={handleTryDifferentFile} $customType="outline">
                                {t`Try different file`}
                            </BaseButton>

                            <BaseButton onClick={exportTaskCsvTemplate} $customType="link">
                                {t`Download sample CSV`}
                            </BaseButton>

                            {taskCsvIssuesFileContent !== null && (
                                <BaseButton onClick={downloadTaskCsvIssues} $customType="link">
                                    {t`Download file with errors`}
                                </BaseButton>
                            )}
                        </Actions>
                    </DescriptionWrapper>
                }
                icon={<ExclamationCircleFilled />}
            />
        </Wrapper>
    );
};
